import { DestroyRef, Injectable, inject } from '@angular/core';

import { Page, ProductService, WINDOW } from '@frontend/vanilla/core';
import { Span as SpanBase, SpanOptions } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { FetchCustomAttributeFunction } from '@opentelemetry/instrumentation-fetch/build/src/fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { XHRCustomAttributeFunction } from '@opentelemetry/instrumentation-xml-http-request/build/src/xhr';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { Subscription } from 'rxjs';

type Span = SpanBase & SpanOptions;

@Injectable({
    providedIn: 'root',
})
export class TracingService {
    private destroyRef = inject(DestroyRef);
    private page = inject(Page);
    private window = inject(WINDOW);
    private product = inject(ProductService);

    async register(url: string, allowedUrlPatterns: string[]): Promise<void> {
        const exporter = new OTLPTraceExporter({ url });
        const processor = new BatchSpanProcessor(exporter);
        const resource = resourceFromAttributes({
            [ATTR_SERVICE_NAME]: `${this.product.current.name}-web`,
            [ATTR_SERVICE_VERSION]: this.window.VERSION,
        });

        const provider = new WebTracerProvider({
            spanProcessors: [processor],
            resource,
        });

        provider.register();

        const instrumentationSubscription = this.registerInstrumentations(allowedUrlPatterns);
        const productSubscription = this.product.productChanged.subscribe(() => {
            resource.attributes[ATTR_SERVICE_NAME] = `${this.product.current.name}-web`;
        });

        this.destroyRef.onDestroy(() => {
            provider.shutdown();

            instrumentationSubscription.unsubscribe();
            productSubscription.unsubscribe();
        });
    }

    private registerInstrumentations(allowedUrlPatterns: string[]): Subscription {
        const sanitizedUrlPatterns = allowedUrlPatterns
            .map((path) =>
                path
                    // placeholders replacements
                    .replace('{origin}', this.window.location.origin)
                    .replace('{language}', this.page.lang)
                    // escape special regex characters
                    .replace(/\./g, String.raw`\.`)
                    .replace(/\//g, String.raw`\/`),
            )
            .join('|');

        const ignoreUrls = [new RegExp(`^(?!.*(${sanitizedUrlPatterns})).*$`)];

        const unsubscribe = registerInstrumentations({
            instrumentations: [
                new FetchInstrumentation({
                    ignoreUrls,
                    applyCustomAttributesOnSpan: this.processFetchSpan,
                    propagateTraceHeaderCorsUrls: /.*/,
                }),
                new XMLHttpRequestInstrumentation({
                    ignoreUrls,
                    applyCustomAttributesOnSpan: this.processXhrSpan,
                    propagateTraceHeaderCorsUrls: /.*/,
                }),
            ],
        });

        return new Subscription(unsubscribe);
    }

    private processFetchSpan: FetchCustomAttributeFunction = (span, _, response) => {
        this.setName(span);

        this.setCloudflareStatus(span, (name) => {
            if (response instanceof Response) {
                return response.headers.get(name);
            }

            return null;
        });
    };

    private processXhrSpan: XHRCustomAttributeFunction = (span, xhr) => {
        this.setName(span);

        this.setCloudflareStatus(span, (name) => xhr.getResponseHeader(name));
    };

    private setCloudflareStatus(span: Span, getHeader: (name: string) => string | null): void {
        const cacheStatus = getHeader('CF-Cache-Status');

        if (cacheStatus) {
            // Refer to cloudflare cache response https://developers.cloudflare.com/cache/concepts/cache-responses/
            // we consider the following as cache hits as cloudflare is returning the cached value it has being stale or not
            const cache = cacheStatus === 'HIT' || cacheStatus === 'STALE' || cacheStatus === 'REVALIDATED' || cacheStatus === 'UPDATING';

            span.setAttribute('http.cache', cache);
            span.setAttribute('http.cache_provider', 'cloudflare');
            span.setAttribute('http.cache_status', cacheStatus);
        } else {
            span.setAttribute('http.cache', false);
        }
    }

    private setName(span: Span & SpanOptions): void {
        const attributes = span.attributes ?? {};

        const method = attributes['http.method'];
        const url = attributes['http.url'];

        // we know for sure these will exist as the span was created by
        if (typeof method === 'string' && typeof url === 'string') {
            let path = url;

            if (url.startsWith('/')) {
                path = url;
            } else {
                try {
                    const { pathname } = new URL(url);

                    path = pathname;
                } catch {
                    // do nothing
                }
            }

            const langSegment = `/${this.page.lang}/`;

            if (path.startsWith(langSegment)) {
                path = path.replace(langSegment, '/{culture}/');
            }

            span.updateName(`${method} ${path}`.trim());
        }
    }
}
